// dashboard/chat/index.tsx
import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, UncontrolledTooltip } from "reactstrap";

// components
import AppSimpleBar from "../../../components/AppSimpleBar";

// interfaces
import { useAuth } from "../../../context/AuthContext";
import { IUserTypes, regionList, tagListBot } from "../../../types/contacts";

// hook
import { useChats } from "../../../hooks/useChats";

import { useMessages } from "../../../context/MessagesContext";
import { useChat } from "../../../context/ChatContext";
import { useContacts } from "../../../hooks/useContacts";

import Loader from "../../../components/Loader";
import { toTimestampSafe } from "../../../utils";
import SelectRegion from "../Chats/SelectRegion";
import { ChatTags } from "../Chats/ChatTags";
import RobotMessages from "./RobotMessages";
import { id } from "date-fns/locale";

const Index = () => {

  const { user } = useAuth();
  const { chatListContactMenu } = useChats();

  //noAssignedContacts
  /** ------------------- FETCH FAVORITES/CONTACTS ------------------- **/
  const { sessions, contactNoAssigned, loadSessionsActive, loadContactAssigned } = useMessages();
  const { isAppReady, selectedChatId, handleSelectChat, chatUserDetails } = useChat();
  const { contactsMap, contacts } = useContacts();

  const [searchTerm, setSearchTerm] = useState("");

  const [noAssignedContacts, setNoAssignedContacts] = useState<IUserTypes[]>([]);

  const [directFull, setDirectFull] = useState<IUserTypes[]>([]);
  const [directActive, setDirectActive] = useState<IUserTypes[]>([]);
  const [directCompleted, setDirectCompleted] = useState<IUserTypes[]>([]);
  const [directInatInactivity, setDirectInatInactivity] = useState<IUserTypes[]>([]);

  const [selectedTagId, setSelectedTagId] = useState<number>(0);
  const [selectedRegionId, setSelectedRegionId] = useState<number>(-1);

  const showAll = selectedTagId === 0;       //Todas as conversas
  const showActive = selectedTagId === 1;    //Conversa andamento
  const showCompleted = selectedTagId === 2; //Conversa Completada
  const showInatInactivity = selectedTagId === 3;  //Conversa Duvidas

  const filterBySearch = useCallback((users: IUserTypes[]) => {

    if (!searchTerm.trim()) return users;

    const term = searchTerm.toLowerCase();

    return users.filter(u =>
      (u.name || "").toLowerCase().includes(term) ||
      (u.fullName || "").toLowerCase().includes(term) ||
      (u.phoneNumber || "").toLowerCase().includes(term)
    );

  }, [searchTerm]);

  const applyFilters = useCallback((users: IUserTypes[]) => {

    let filtered = users;

    // 🔍 busca
    filtered = filterBySearch(filtered);

    // 🌎 região
    if (selectedRegionId >= 0) {
      filtered = filtered.filter(user => {
        const contact = contactsMap.get(user.id);

        // trata null/undefined como 0 (Sem Região)
        const idRegiao = contact?.idRegiao ?? 0;

        return idRegiao === selectedRegionId;
      });
    }

    return filtered;

  }, [selectedRegionId, contactsMap, filterBySearch]);

  const filteredActiveFull = React.useMemo(() => {
    const filtered = applyFilters(directActive);
    return filtered.sort(
      (a, b) => toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime)
    );
  }, [directActive, applyFilters]);

  const filteredCompletedFull = React.useMemo(() => {
    const filtered = applyFilters(directCompleted);
    return filtered.sort(
      (a, b) => toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime)
    );
  }, [directCompleted, applyFilters]);

  const filteredInatInactivityFull = React.useMemo(() => {
    const filtered = applyFilters(directInatInactivity);
    return filtered.sort(
      (a, b) => toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime)
    );
  }, [directInatInactivity, applyFilters]);

  //1- Pega os dados context, sinalR
  useEffect(() => {
    let ignore = false;

    const fetchBaseData = async () => {
      if (!user?.id || !user.idEnterprise) return;

      try {
        await Promise.all([
          loadContactAssigned(), //Sessoes de coversas novas
          loadSessionsActive(), //Sessoes de coversas em Andamento
        ]);

        if (ignore) return;

      } catch (error) {
        console.error("[Robot][Leftbar] Erro ao buscar dados:", error);
      }
    };

    fetchBaseData();

    return () => {
      ignore = true;
    };
  }, [user?.id, user?.idEnterprise]);

  //2- Trata da exibição das conversas no menu (ROBO)
  useEffect(() => {
    if (!user?.id || sessions.length === 0 || contacts.length === 0) return;

    // 🔢 Constantes
    const STEP_CANCELED = 102;
    const STEP_CLOSED = 90;
    const STEP_COMPLETED_START = 99;
    const STEP_PROBLEM = 200;

    const idUserIA = 1;

    //LOG 🟡 DÚVIDAS (veio do robô)
    // sessions.forEach(s => {
    //   console.log({
    //     id: s.id,
    //     idContact: s.idContact,
    //     isActive: s.isActive,
    //     aiCurrentStep: s.aiCurrentStep,
    //     aiFallbackToHuman: s.aiFallbackToHuman,
    //     Data: s.aiLastInteraction
    //   });
    // });
    // sessions
    //   .filter(s =>
    //     Number(s.aiFallbackToHuman ?? 0) === 0 && // não caiu para humano
    //     s.isActive === true                      // sessão ativa
    //   )
    //   .forEach(s => {
    //     console.log({
    //       id: s.id,
    //       idContact: s.idContact,
    //       isActive: s.isActive,
    //       aiCurrentStep: s.aiCurrentStep,
    //       aiFallbackToHuman: s.aiFallbackToHuman,
    //       DataLastInteraction: s.aiLastInteraction,
    //       DataClosed: s.closedAt
    //     });
    //   });

    // const limitMs = 12 * 60 * 60 * 1000;

    // sessions
    //   .filter(s =>
    //     Number(s.aiFallbackToHuman ?? 0) === 0 && // não virou humano
    //     Number(s.isActive) === 1 &&               // ativo
    //     s.aiLastInteraction &&
    //     s.aiCurrentStep >= 0 && s.aiCurrentStep < 90 &&
    //     (Date.now() - new Date(s.aiLastInteraction).getTime()) < limitMs // 🔥 menos de 12h
    //   )
    //   .forEach(s => {
    //     console.log(s);
    //   });



    // 🧠 Última sessão por contato (mais performático)
    const sessionMap = new Map<number, any>();
    const LIMIT_MS = 12 * 60 * 60 * 1000;
    const NOW = Date.now();

    for (let i = 0; i < sessions.length; i++) {
      const s = sessions[i];
      const contactId = s.idContact;

      if (s.aiLastInteraction) {
        const last = new Date(s.aiLastInteraction).getTime();

        if (NOW - last >= LIMIT_MS) {
          continue; // ❌ mais de 12h
        }
      }

      const existing = sessionMap.get(contactId);

      if (!existing || new Date(s.createdAt) > new Date(existing.createdAt)) {
        sessionMap.set(contactId, s);
      }
    }

    // 🤖 IDs válidos do robô
    const robotContactsIds = new Set<number>();

    sessionMap.forEach(s => {
      if (
        Number(s.isAiActive) === 1 &&          // 🤖 é IA
        Number(s.idUser) === idUserIA &&       // 👤 dono é IA
        Number(s.aiFallbackToHuman ?? 0) === 0 // ❌ não virou humano
      ) {
        robotContactsIds.add(Number(s.idContact));
      }
    });

    // 📇 Lista final
    const contactListFull: IUserTypes[] = [];

    for (let i = 0; i < contacts.length; i++) {
      const c = contacts[i];
      if (robotContactsIds.has(c.id)) {
        contactListFull.push(c);
      }
    }

    // 🔥 Já separa em uma passada só (evita 3 filters depois)
    const active: IUserTypes[] = [];
    const completed: IUserTypes[] = [];
    const inatInactivity: IUserTypes[] = [];

    for (let i = 0; i < contactListFull.length; i++) {
      const chat = contactListFull[i];
      const session = sessionMap.get(chat.id);

      if (!session) continue;

      const step = Number(session.aiCurrentStep ?? -1);
      const isActive = Number(session.isActive);
      const marked = Number(chat.marked);

      // ❌ BLOQUEIOS GLOBAIS      
      if (step === STEP_CANCELED) continue;

      // 🟢 ATIVAS
      if (step >= 0 && step < STEP_CLOSED) {
        active.push(chat);
        continue;
      }

      // ❌ NÃO ENTRA EM COMPLETED SE marked = 1
      if (isActive === 0 && marked === 1) continue;

      // ✅ COMPLETED
      if (step >= STEP_COMPLETED_START && step < STEP_PROBLEM) {
        completed.push(chat);
        continue;
      }

    }

    // 🔥 Atualiza estado (mínimo possível)
    setDirectFull(contactListFull);
    setDirectActive(active);
    setDirectCompleted(completed);
    setDirectInatInactivity(inatInactivity);

  }, [
    chatListContactMenu,
    contactNoAssigned,
    sessions,
    contacts,
    user?.id,
  ]);


  /** ------------------- RENDER ------------------- **/
  return (
    <>
      <div>
        <div className="px-2 pt-2">
          <div className="d-flex align-items-start">
            <div className="flex-grow-1">
              <h4 className="mb-4">🤖 Conversas</h4>
            </div>
            <div className="flex-shrink-0">
              {/* <div id="add-contact">
                <AddButton id="add-contact" onClick={openInviteModal} />
              </div> */}
              <UncontrolledTooltip target="add-contact" placement="bottom"
                transition={{ timeout: 1 }}
                delay={0}
              >
                Novo Contato
              </UncontrolledTooltip>
            </div>
          </div>

          <Form>
            <div className="input-group">
              <SelectRegion
                selectedRegionId={selectedRegionId}
                regions={regionList}
                onSelectRegion={(id: number) => setSelectedRegionId(Number(id))}
              />
            </div>

            <div className="input-group mb-2">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                type="text"
                className="form-control bg-light border-0 pe-0"
                placeholder="pesquisar aqui.."
              />
              <Button color="light" type="button" id="searchbtn-addon">
                <i className="bx bx-search align-middle"></i>
              </Button>
            </div>
            <div className="input-group mb-3">
              <ChatTags
                tags={tagListBot}
                value={selectedTagId}
                onChange={(id: number) => setSelectedTagId(id)}
              />
            </div>


          </Form>
        </div>{" "}

        {!isAppReady && <Loader />}

        <AppSimpleBar className="chat-room-list">

          <>
            {/* Conversas ativas  */}
            {(showAll || showActive) && filteredActiveFull.length > 0 && (
              <RobotMessages
                key="robotMessagesActive"
                title="Conversas ativas"
                users={filteredActiveFull}
                openAddContact={() => null}
                selectedChatId={selectedChatId as number}
                onSelectChat={handleSelectChat}
              />
            )}

            {/* Conversas completadas  */}
            {(showAll || showCompleted) && filteredCompletedFull.length > 0 && (
              <RobotMessages
                key="robotMessagesCompleted"
                title="Conversas completadas"
                users={filteredCompletedFull}
                openAddContact={() => null}
                selectedChatId={selectedChatId as number}
                onSelectChat={handleSelectChat}
              />
            )}

            {/* Conversas com problema  */}
            {(showAll || showInatInactivity) && filteredInatInactivityFull.length > 0 && (
              <RobotMessages
                key="robotMessagesInatInactivity"
                title="Conversas sem Interatividade +12hrs"
                users={filteredInatInactivityFull}
                openAddContact={() => null}
                selectedChatId={selectedChatId as number}
                onSelectChat={handleSelectChat}
              />
            )}

          </>

        </AppSimpleBar>
      </div>

    </>
  );
};

export default Index;
